import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { showError } from '../utils/notifications';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';

function AdminMonitoreo() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [caUsers, setCaUsers] = useState([]); // Solo CAs con pocos tickets (filtrados)
  const [allCaUsers, setAllCaUsers] = useState([]); // Todos los CAs para estadísticas
  const [activeSection, setActiveSection] = useState('admin-monitoreo');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lowTicketCAs, setLowTicketCAs] = useState(new Set());
  const [notifiedCAs, setNotifiedCAs] = useState(new Set()); // Para notificar solo una vez
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const navigate = useNavigate();

  // Umbral mínimo de tickets de AUTOS
  const TICKET_THRESHOLD = 12;

  // Función para crear sonido de respaldo usando Web Audio API
  const createBeepSound = () => {
    try {
      console.log('🔊 Creando sonido de respaldo...');
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Crear oscilador para el sonido
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Configurar el sonido
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configurar frecuencia (sonido de alerta)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      
      // Configurar volumen
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      // Reproducir el sonido
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      console.log('✅ Sonido de respaldo creado exitosamente');
    } catch (error) {
      console.error('❌ No se pudo crear sonido de respaldo:', error);
      // Último recurso: vibración en dispositivos móviles
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200, 100, 200]);
        console.log('📳 Vibración activada como respaldo');
      }
    }
  };

  // Función para mostrar notificación temporal (SOLO UNA VEZ)
  const showNotification = useCallback((caData) => {
    // Verificar si ya se notificó este CA O si está inactivo
    if (notifiedCAs.has(caData.id) || caData.status !== 'Activo') {
      return; // No notificar de nuevo o si está inactivo
    }

    console.log(`🚨 NOTIFICACIÓN: ${caData.nombre} (${caData.numberCa}) - ${caData.ticketsAutos} tickets`);

    const notificationId = `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const notification = {
      id: notificationId,
      ca: caData,
      timestamp: new Date().toLocaleTimeString(),
      createdAt: Date.now()
    };

    // Reproducir sonido de notificación con mejor manejo
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.9;
      audio.preload = 'auto';
      
      // Intentar reproducir el sonido
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('🔊 Sonido de notificación reproducido correctamente');
          })
          .catch((error) => {
            console.warn('⚠️ No se pudo reproducir el sonido automáticamente:', error);
            // Crear sonido de respaldo usando Web Audio API
            createBeepSound();
          });
      }
    } catch (error) {
      console.error('❌ Error con el archivo de audio:', error);
      // Crear sonido de respaldo
      createBeepSound();
    }

    // Marcar como notificado
    setNotifiedCAs(prev => new Set([...prev, caData.id]));
    setNotifications(prev => [...prev, notification]);

    // Programar eliminación después de 20 segundos
    setTimeout(() => {
      console.log(`🗑️ Eliminando notificación: ${notificationId}`);
      setNotifications(prevNotifications => {
        const filtered = prevNotifications.filter(n => n.id !== notificationId);
        console.log(`📊 Notificaciones restantes: ${filtered.length}`);
        return filtered;
      });
    }, 20000);
  }, [notifiedCAs]);

  // Efecto para verificar tickets de AUTOS y mostrar notificaciones
  useEffect(() => {
    allCaUsers.forEach(ca => {
      const currentTicketsAutos = ca.ticketsAutos || 0; // Solo tickets de autos
      const wasLow = lowTicketCAs.has(ca.id);
      const isLowNow = currentTicketsAutos < TICKET_THRESHOLD;

      // Si el CA acaba de quedarse con pocos tickets de autos Y no se ha notificado
      if (isLowNow && !wasLow) {
        showNotification(ca);
        setLowTicketCAs(prev => new Set([...prev, ca.id]));
      }
      // Si el CA ya no tiene pocos tickets de autos
      else if (!isLowNow && wasLow) {
        setLowTicketCAs(prev => {
          const newSet = new Set(prev);
          newSet.delete(ca.id);
          return newSet;
        });
        // Resetear la notificación para permitir futuras alertas
        setNotifiedCAs(prev => {
          const newSet = new Set(prev);
          newSet.delete(ca.id);
          return newSet;
        });
      }
    });
  }, [allCaUsers, lowTicketCAs, showNotification]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verificar usuario y rol
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          const userData = userDoc.data();
          setUser(currentUser);
          setUserRole(userData.rol || 'Usuario');

          // Solo administradores pueden ver esta página
          if (userData.rol !== 'Admin') {
            showError("Acceso denegado", "Solo administradores pueden acceder al monitoreo");
            navigate('/dashboard');
            return;
          }

          // Configurar listener en tiempo real para CAs
          const caQuery = query(collection(db, 'usuarios'), where('rol', '==', 'CA'));
          const unsubscribeSnapshot = onSnapshot(caQuery, (querySnapshot) => {
            const usersList = [];
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              usersList.push({
                id: doc.id,
                nombre: data.nombre || 'N/A',
                email: data.email || doc.id,
                numberCa: data.numberCa || 'N/A',
                zona: data.zona || 'N/A',
                status: data.status === 1 ? 'Activo' : 'Inactivo',
                ticketsAutos: data.ticketsAuto || data.ticketsDisponibles || 0, // Campo correcto: ticketsAuto
                ticketsMotos: data.ticketsMoto || 0, // Campo correcto: ticketsMoto
                balance: data.balance || 0,
                tipoVehiculos: data.tipoVehiculos || 'autos_y_motos',
                ultimaConexion: data.ultimaConexion || 'N/A',
                serial: data.serial || 'N/A'
              });
            });
            
            // Guardar TODOS los CAs para estadísticas y notificaciones
            setAllCaUsers(usersList);
            
            // FILTRAR: Solo mostrar CAs ACTIVOS que tienen tickets (mayor a 0) de motos O autos
            const filteredList = usersList.filter(ca => {
              const hasTicketsAutos = (ca.ticketsAutos || 0) > 0;
              const hasTicketsMotos = (ca.ticketsMotos || 0) > 0;
              
              // Solo mostrar CAs que tienen al menos 1 ticket de motos O autos
              return ca.status === 'Activo' && (hasTicketsAutos || hasTicketsMotos);
            });
            
            // Ordenar por tickets de autos disponibles (menor a mayor) para destacar los críticos
            filteredList.sort((a, b) => a.ticketsAutos - b.ticketsAutos);
            setCaUsers(filteredList);
            
            // Actualizar estado de conexión y timestamp
            setIsConnected(true);
            setLastUpdate(new Date());
          }, (error) => {
            console.error("Error en el listener de Firebase:", error);
            setIsConnected(false);
          });

          // Limpiar listener cuando el componente se desmonte
          return () => unsubscribeSnapshot();

        } catch (error) {
          console.error("Error fetching CA users:", error);
          showError("Error", "No se pudieron cargar los datos de monitoreo");
        } finally {
          setLoading(false);
        }
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate, showNotification]);

  // Función para obtener el color de la fila según el estado de tickets de AUTOS
  const getRowColor = (ticketsAutos) => {
    if (ticketsAutos < TICKET_THRESHOLD) return 'bg-red-50 border-red-200';
    if (ticketsAutos < 25) return 'bg-yellow-50 border-yellow-200';
    return 'bg-white';
  };

  // Función para obtener el color del badge de tickets de AUTOS
  const getTicketBadgeColor = (ticketsAutos) => {
    if (ticketsAutos < TICKET_THRESHOLD) return 'bg-red-100 text-red-800';
    if (ticketsAutos < 25) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title={`🚗🏍️ CAs con Tickets ${isConnected ? '🟢' : '🔴'}`}
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
      
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <h1 className="text-xl font-bold text-gray-900 mr-4">
                  🚗🏍️ CAs ACTIVOS con Tickets (Sin Ceros)
                </h1>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
                  <span className={`text-xs font-medium ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
                    {isConnected ? 'Conectado' : 'Desconectado'}
                  </span>
                  {lastUpdate && (
                    <span className="text-xs text-gray-500">
                      | Última actualización: {lastUpdate.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">{userRole || "Usuario"}</p>
              </div>
            </div>
          </header>

          {/* Notificaciones flotantes MEJORADAS */}
          <div className="fixed top-20 right-4 z-50 space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-red-600 text-white px-10 py-8 rounded-2xl shadow-2xl animate-bounce border-4 border-red-800"
                style={{ minWidth: '500px', maxWidth: '600px' }}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-8xl animate-pulse">🚨</span>
                  </div>
                  <div className="ml-8 flex-1">
                    <p className="text-3xl font-extrabold mb-2">
                      ¡TICKETS BAJOS!
                    </p>
                    
                    {/* CA y Zona MUY VISIBLES */}
                    <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-3">
                      <p className="text-4xl font-bold text-yellow-300">
                        📍 {notification.ca.numberCa}
                      </p>
                      <p className="text-2xl font-semibold">
                        🏢 {notification.ca.nombre}
                      </p>
                      {notification.ca.zona !== 'N/A' && (
                        <p className="text-xl font-medium text-yellow-200">
                          🗺️ {notification.ca.zona}
                        </p>
                      )}
                    </div>
                    
                    <p className="text-xl font-bold text-red-200">
                      🚗 Solo {notification.ca.ticketsAutos} tickets de autos
                    </p>
                                         <p className="text-sm opacity-90 mt-2">
                       ⏰ {notification.timestamp} | Auto-elimina en 20s
                     </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Content */}
          <main className="p-6">
            {/* Estadísticas Rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total CAs</p>
                    <p className="text-2xl font-bold text-gray-900">{allCaUsers.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow border-2 border-orange-200">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <span className="text-2xl">🔍</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">🚗🏍️ CAs con Tickets</p>
                    <p className="text-2xl font-bold text-orange-600">{caUsers.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <span className="text-2xl">🚨</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">🚗 Tickets Críticos</p>
                    <p className="text-2xl font-bold text-red-600">
                      {allCaUsers.filter(ca => ca.ticketsAutos < TICKET_THRESHOLD).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <span className="text-2xl">⚠️</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">🚗 Tickets Bajos</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {allCaUsers.filter(ca => ca.ticketsAutos >= TICKET_THRESHOLD && ca.ticketsAutos < 30).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <span className="text-2xl">✅</span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">🚗 Con Suficientes</p>
                    <p className="text-2xl font-bold text-green-600">
                      {allCaUsers.filter(ca => ca.ticketsAutos >= 30).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-700">
                    🚗🏍️ CAs ACTIVOS con Tickets (Sin Ceros)
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    ✅ Solo CAs ACTIVOS que tienen tickets (mayor a 0) de motos O autos | 🚫 Ignora CAs con 0 tickets de ambos tipos
                  </p>
                </div>
                <button
                  onClick={() => {
                    // Simular notificación de prueba - CA ACTIVO
                    const testCA = {
                      id: 'test_' + Date.now(), // ID único para permitir múltiples pruebas
                      nombre: 'CA de Prueba',
                      numberCa: 'CA00',
                      zona: 'Z 1-S01',
                      status: 'Activo',
                      ticketsAutos: 5
                    };
                    showNotification(testCA);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm"
                  title="Probar notificación"
                >
                  🔔 Probar Notificación
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CA
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        🚗 Tickets AUTOS
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        🏍️ Tickets Motos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zona
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo Vehículos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado CA
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {caUsers.map((caUser) => (
                      <tr 
                        key={caUser.id} 
                        className={`${getRowColor(caUser.ticketsAutos)} hover:bg-gray-50 transition-colors duration-200`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {caUser.ticketsAutos < TICKET_THRESHOLD ? (
                              <span className="text-3xl animate-pulse">🚨</span>
                            ) : caUser.ticketsAutos < 25 ? (
                              <span className="text-3xl">⚠️</span>
                            ) : (
                              <span className="text-3xl">✅</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {caUser.numberCa}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {caUser.nombre}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTicketBadgeColor(caUser.ticketsAutos)}`}>
                            🚗 {caUser.ticketsAutos} tickets
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                            🏍️ {caUser.ticketsMotos}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {caUser.zona}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {caUser.tipoVehiculos === 'autos_y_motos' ? '🚗🏍️ Autos y Motos' :
                           caUser.tipoVehiculos === 'solo_autos' ? '🚗 Solo Autos' :
                           caUser.tipoVehiculos === 'solo_motos' ? '🏍️ Solo Motos' : 
                           caUser.tipoVehiculos}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${caUser.balance.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            caUser.status === 'Activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {caUser.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {caUsers.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                          🎉 ¡Excelente! Todos los CAs ACTIVOS con tickets tienen suficientes tickets de autos (≥ 30).
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Leyenda */}
                          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">
                📊 Información del Monitoreo FILTRADO de CAs CON TICKETS
              </h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• <span className="font-medium">🚗🏍️ Monitoreo específico:</span> Solo CAs que tienen tickets (mayor a 0) de motos O autos</p>
                <p>• <span className="font-medium">🔍 Filtro aplicado:</span> Solo CAs ACTIVOS con al menos 1 ticket de motos O autos</p>
                <p>• <span className="font-medium">🚫 Ignorados:</span> CAs con 0 tickets de motos Y 0 tickets de autos</p>
                <p>• <span className="font-medium">📊 Estadísticas:</span> Se calculan con TODOS los CAs del sistema</p>
                <p>• <span className="font-medium">Actualización en tiempo real:</span> Los datos se actualizan automáticamente</p>
                <p>• <span className="font-medium">Notificaciones:</span> Se muestran cuando un CA baja de {TICKET_THRESHOLD} tickets de autos</p>
                <p>• <span className="font-medium">Duración de notificaciones:</span> 20 segundos</p>
                <p>• <span className="font-medium">Frecuencia:</span> Solo UNA notificación por CA hasta que se resuelva</p>
                <p>• <span className="font-medium">Rangos de color:</span> 🔴 Crítico (&lt;{TICKET_THRESHOLD}) | 🟡 Bajo (&lt;25) | 🟢 Normal (≥25)</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default AdminMonitoreo; 